import { useAxios } from '@/context/axiosContext'
import {
  GolfPriceItem,
  setGraphData,
  setListData,
  setMonthAvgPriceByRegion,
  setQuarterAvgPrice,
  setRegionAvgPrice,
  setYearAvgPrice,
  setWeekAvgPriceByRegion,
  setWeekGraphData,
  setSearchParams
} from '@/redux/slices/golfSearchSlice'
import { RootState } from '@/redux/store'
import { useEffect, useState, useCallback, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'

export interface PeriodAvgData {
  areaCd: string
  areaNm: string
  n01: number
  n02: number
  m01: number
  m02: number
  month: string
  year: string
  quarter: string
  week: string
}

export interface regionAvgPriceItem {
  areaCd: string
  areaNm: string
  n01: number
  n02: number
  m01: number
  m02: number
}

interface GolfPriceResponse {
  common: {
    resultCode: string
    resultMsg: string
  }
  data: {
    listData: GolfPriceItem[] // 골프장 요금 목록
    monthAvgPriceData: PeriodAvgData[] // 월평균 가격 데이터
    monthAvgPriceByRegionData: PeriodAvgData[] // 지역별 월평균 가격 데이터
    regionAvgPriceData: regionAvgPriceItem[] //지역별 현재 가격 데이터
    quarterAvgPriceData: PeriodAvgData[] // 분기 평균 가격 데이터
    yearAvgPriceData: PeriodAvgData[] // 연 평균 가격 데이터
    weekAvgPriceData: PeriodAvgData[] // 주평균 가격 데이터
    weekAvgPriceByRegionData: PeriodAvgData[] // 지역별 주평균 가격 데이터

    weekBizCdAvgPriceData: PeriodAvgData[] // 골프장 주 평균 가격 데이터
  }
}

export const mapMonthDataToGraph = (
  monthData: PeriodAvgData[],
  monthsArr: string[],
): number[][] => {
  const updatedGraphData = [
    new Array(12).fill(0), // n01
    new Array(12).fill(0), // n02
    new Array(12).fill(0), // m01
    new Array(12).fill(0), // m02
  ]

  if (monthData && Array.isArray(monthData)) {
    monthData.forEach((item) => {
      const formattedMonth = `${item.year.slice(-2)}-${item.month.padStart(2, '0')}`
      const monthIndex = monthsArr.findIndex(
        (month) => month === formattedMonth,
      )

      if (monthIndex >= 0 && monthIndex < 12) {
        updatedGraphData[0][monthIndex] = item.n01 ?? 0
        updatedGraphData[1][monthIndex] = item.n02 ?? 0
        updatedGraphData[2][monthIndex] = item.m01 ?? 0
        updatedGraphData[3][monthIndex] = item.m02 ?? 0
      }
    })
  }
  return updatedGraphData
}

export const mapWeekDataToGraph = (
  weekData: PeriodAvgData[],
  weekArr: string[],
): number[][] => {
  const updatedGraphData = [
    new Array(weekArr.length).fill(0), // n01
    new Array(weekArr.length).fill(0), // n02
    new Array(weekArr.length).fill(0), // m01
    new Array(weekArr.length).fill(0), // m02
  ]

  if (weekData && Array.isArray(weekData)) {
    weekData.forEach((item) => {
      const { year, week } = item
      const weekKey = `${year}-${String(week).padStart(2, '0')}`
      const weekIndex = weekArr.findIndex((w) => w === weekKey)

      if (weekIndex >= 0) {
        updatedGraphData[0][weekIndex] = item.n01 ?? 0
        updatedGraphData[1][weekIndex] = item.n02 ?? 0
        updatedGraphData[2][weekIndex] = item.m01 ?? 0
        updatedGraphData[3][weekIndex] = item.m02 ?? 0
      }
    })
  }
  return updatedGraphData
}

export const mapPeriodDataToGraph = (
  periodData: PeriodAvgData[],
  periodType: 'quarter' | 'year',
): number[][] => {
  const currentYear = new Date().getFullYear()
  const periodLength = periodType === 'quarter' ? 12 : 5
  const updatedGraphData = [
    new Array(periodLength).fill(0), // n01
    new Array(periodLength).fill(0), // n02
    new Array(periodLength).fill(0), // m01
    new Array(periodLength).fill(0), // m02
  ]

  const sortedPeriodData = periodData.sort((a, b) => {
    if (periodType === 'quarter') {
      const yearDiff = parseInt(b.year) - parseInt(a.year)
      return yearDiff !== 0
        ? yearDiff
        : parseInt(b.quarter) - parseInt(a.quarter)
    } else {
      return parseInt(b.year) - parseInt(a.year)
    }
  })

  if (sortedPeriodData && Array.isArray(sortedPeriodData)) {
    sortedPeriodData.forEach((item) => {
      const periodIndex =
        periodType === 'quarter'
          ? (parseInt(item.year) - currentYear) * 4 +
            (parseInt(item.quarter) - 1) +
            periodLength -
            1
          : parseInt(item.year) - currentYear + periodLength - 1

      if (periodIndex >= 0 && periodIndex < periodLength) {
        updatedGraphData[0][periodIndex] = item.n01 ?? 0
        updatedGraphData[1][periodIndex] = item.n02 ?? 0
        updatedGraphData[2][periodIndex] = item.m01 ?? 0
        updatedGraphData[3][periodIndex] = item.m02 ?? 0
      }
    })
  }
  return updatedGraphData
}

const getMonday = (date: Date): Date => {
  const d = new Date(date);
  const day = d.getDay();// 0(일) ~ 6(토)
  const diff = (day === 0 ? -6 : 1) - day;// 월요일 기준 보정
  d.setDate(d.getDate() + diff);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());// 시간 제외
}

export const generatePeriod = (
  type: 'month' | 'quarter' | 'year' | 'week',
): string[] => {
  const periodArr: string[] = []
  const currentDate = new Date()

  if (type === 'week') {
     for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate);
      date.setDate(date.getDate() - i * 7); // i주 전
    
      const monday = getMonday(date);
      const year = monday.getFullYear();
      const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
      const firstMonday = getMonday(firstDayOfYear);
      const diffTime = date.getTime() - firstMonday.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const week =  Math.floor(diffDays / 7) + 1;
    
      periodArr.unshift(`${year}-${String(week).padStart(2, '0')}`);
    }
  } else if (type === 'month') {
    //월별 데이터 해당 월 시작부터로 변경 (25.04.10 변경 - 최안나)
    const start = new Date(currentDate.getFullYear(), 0, 1)
    const end = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
    const current = new Date(start)

    while (current <= end) {
      const year = current.getFullYear().toString().slice(-2)
      const month = (current.getMonth() + 1).toString().padStart(2, '0')
      periodArr.push(`${year}-${month}`)
      current.setMonth(current.getMonth() + 1)
    }
  } else if (type === 'quarter') {
    for (let i = 0; i < 12; i++) {
      const prevQuarter = new Date(currentDate)
      prevQuarter.setMonth(prevQuarter.getMonth() - i * 3)
      const quarter = Math.floor(prevQuarter.getMonth() / 3) + 1
      const year = prevQuarter.getFullYear().toString().slice(-2)
      periodArr.unshift(`${year}-${quarter}Q`)
    }
  } else if (type === 'year') {
    for (let i = 0; i < 5; i++) {
      const prevYear = new Date(currentDate)
      prevYear.setFullYear(currentDate.getFullYear() - i)
      periodArr.unshift(prevYear.getFullYear().toString())
    }
  }

  return periodArr
}

export const useGolfSearchData = () => {
  const dispatch = useDispatch()
  const axios = useAxios()

  const searchParams = useSelector(
    (state: RootState) => state.golfSearch.searchParams,
  )

  // 기본 holeType을 '18'로 설정
  const holeType = searchParams.holeType || '18'

  const [data, setData] = useState<GolfPriceItem[]>([])
  const [isInitialLoading, setIsInitialLoading] = useState(false)
  
  // 로딩 상태 관리를 위한 ref
  const loadingCountRef = useRef(0)
  const isInitializedRef = useRef(false)

  // 로딩 관리 함수들


  // searchParams 메모화로 무한 루프 방지
  const memoizedSearchParams = useMemo(() => {
    const params = { ...searchParams }
    if (params.membershipYn === 'A') {
      params.membershipYn = null
    }
    {/* 
    // 최초 로딩 시 holeType이 없으면 '18'로 설정
    if (!params.holeType) {
      params.holeType = '18'
    }
    */}
    return params
  }, [searchParams])

  // API 호출 함수들을 useCallback으로 메모화
  const getGolfPriceList = useCallback(async () => {
    try {
      const { data: responseData } = await axios.get<GolfPriceResponse>(
        '/golf/list',
        { params: memoizedSearchParams },
      )

      const { listData } = responseData.data
      const { resultCode, resultMsg } = responseData.common
      
      if (resultCode !== 'G0200') {
        console.error(`${resultCode} :: ${resultMsg}`)
        return
      }

      setData(listData)
      dispatch(setListData(listData))
    } catch (error) {
      console.error('Error fetching golf price list:', error)
    } finally {
    }
  }, [axios, memoizedSearchParams, dispatch])

  const getQuarterAvgPrice = useCallback(async () => {
    try {
      const { data: responseData } = await axios.get<GolfPriceResponse>(
        '/golf/quarterStatList',
        { params: memoizedSearchParams }
      )
      const { quarterAvgPriceData } = responseData.data
      const quarterGraphData = mapPeriodDataToGraph(quarterAvgPriceData, 'quarter')
      dispatch(setQuarterAvgPrice(quarterGraphData))
    } catch (error) {
      console.error('Error fetching quarter avg price:', error)
    } finally {

    }
  }, [axios, memoizedSearchParams, dispatch])

  const getYearAvgPrice = useCallback(async () => {
    try {
      const { data: responseData } = await axios.get<GolfPriceResponse>(
        '/golf/yearStatList',
        { params: memoizedSearchParams }
      )
      const { yearAvgPriceData } = responseData.data
      const yearGraphData = mapPeriodDataToGraph(yearAvgPriceData, 'year')
      dispatch(setYearAvgPrice(yearGraphData))
    } catch (error) {
      console.error('Error fetching year avg price:', error)
    } finally {

    }
  }, [axios, memoizedSearchParams, dispatch,])

  const getMonthAvgPrice = useCallback(async () => {
    try {

      const { data: responseData } = await axios.get<GolfPriceResponse>(
        '/golf/monthStatList',
        { params: memoizedSearchParams }
      )
      const { monthAvgPriceData } = responseData.data
      const monthsArr = generatePeriod('month')
      const updatedGraphData = mapMonthDataToGraph(monthAvgPriceData, monthsArr)
      dispatch(setGraphData(updatedGraphData))
    } catch (error) {
      console.error('Error fetching month avg price:', error)
    } finally {

    }
  }, [axios, memoizedSearchParams, dispatch,])

  const getMonthAvgPriceByRegion = useCallback(async () => {
    try {
      const { data: responseData } = await axios.get<GolfPriceResponse>(
        '/golf/monthRegionStatList',
        { params: memoizedSearchParams }
      )
      const { monthAvgPriceByRegionData } = responseData.data
      dispatch(setMonthAvgPriceByRegion(monthAvgPriceByRegionData))
    } catch (error) {
      console.error('Error fetching month avg price by region:', error)
    } finally {
    }
  }, [axios, memoizedSearchParams, dispatch,])

    const getRegionAvgPrice = useCallback(async () => {
    try {

      const params = {
        ...memoizedSearchParams,
        holeType: '18',
      };

      const { data: responseData } = await axios.get<GolfPriceResponse>(
        '/golf/regionStatList',
        { params, _disableLoading: true}
      )
      const { regionAvgPriceData } = responseData.data
      dispatch(setRegionAvgPrice(regionAvgPriceData))
    } catch (error) {
      console.error('Error fetching region avg price:', error)
    } finally {

    }
  }, [axios, memoizedSearchParams, dispatch,])

  const isFetchingRef = useRef(false);

  const getWeekAvgPrice = useCallback(async (currentHoleType?: string) => {
  if (isFetchingRef.current) return;
  isFetchingRef.current = true;

  try {
    const params = {
      ...memoizedSearchParams,
      ...(holeType !== '18'
        ? { holeType }
        : (currentHoleType || holeType) && { holeType: currentHoleType || holeType }),
    };

    const { data: responseData } = await axios.get<GolfPriceResponse>(
      '/golf/weekStatWeekList',
      { params, _disableLoading: true}
    );
    const { weekAvgPriceData } = responseData.data;
    const weeksArr = generatePeriod('week');
    const updatedGraphData = mapWeekDataToGraph(weekAvgPriceData, weeksArr);
    dispatch(setWeekGraphData(updatedGraphData));
  } catch (error) {
    console.error('Error fetching week avg price:', error);
  } finally {
    isFetchingRef.current = false;
  }
}, [memoizedSearchParams, holeType, dispatch]);

  const [isWeekAvgPriceByRegionLoading, setIsWeekAvgPriceByRegionLoading] = useState(false)

const getWeekAvgPriceByRegion = useCallback(
  async (currentHoleType?: string) => {
    try {
      setIsWeekAvgPriceByRegionLoading(true); // 호출 시작 시 로딩 true

      // 2025.07.09 오늘날짜: getWeekAvgPrice와 동일한 구조로 수정
      const params = {
        ...memoizedSearchParams,
        ...(holeType !== '18'
          ? { holeType }
          : (currentHoleType || holeType) && { holeType: currentHoleType || holeType }),
      };

      const { data: responseData } = await axios.get<GolfPriceResponse>(
        '/golf/weekRegionStatWeekList',
        { params, _disableLoading: true }
      );
      const { weekAvgPriceByRegionData } = responseData.data;
      dispatch(setWeekAvgPriceByRegion(weekAvgPriceByRegionData));
    } catch (error) {
      console.error('Error fetching week avg price by region:', error);
    } finally {
      setIsWeekAvgPriceByRegionLoading(false); // 호출 완료 시 로딩 false
    }
  },
  [axios, memoizedSearchParams, holeType, dispatch] // 2025.07.09 오늘날짜: holeType 추가
);
  
  const refreshWeekData = useCallback(async (newHoleType: string) => {
    await getWeekAvgPrice(newHoleType);
  }, [getWeekAvgPrice]);

  // holeType 변경 함수 - Redux만 업데이트
  const setHoleType = useCallback((newHoleType: string) => {
    dispatch(setSearchParams({ ...searchParams, holeType: newHoleType }));
  }, [dispatch, searchParams]);

  // 최초 필수 데이터 로드
const loadInitialData = useCallback(async () => {
  try {
    // 중요한 데이터부터 순차적으로 로드
    await getGolfPriceList()
    
  } catch (error) {
    console.error('Error loading initial data:', error)
  }
}, [getGolfPriceList])

const loadAdditionalData = useCallback(async () => {
  try {
    // 추가 데이터도 순차적으로 로드 (간격 추가)
    await getWeekAvgPrice('18')
    await getWeekAvgPriceByRegion('18')
    await getRegionAvgPrice()
  } catch (error) {
    console.error('Error loading additional data:', error)
  }
}, [getWeekAvgPrice, getWeekAvgPriceByRegion, getRegionAvgPrice])


  // 최기 진입 시에만 초기화
  useEffect(() => {
    if (!isInitializedRef.current) {
      isInitializedRef.current = true
      
      // holeType이 없으면 '18'로 설정
      if (!searchParams.holeType) {
        dispatch(setSearchParams({ ...searchParams, holeType: '' }));
      } else {
        loadInitialData();
      }

      const timer = setTimeout(() => {
        loadAdditionalData()
      }, 500)
      
      return () => clearTimeout(timer)
    }
  }, [searchParams.holeType]);

  // searchParams 변경 시에만 데이터 재로드 (최초 초기화 이후)
  useEffect(() => {
    if (isInitializedRef.current && searchParams) {
      loadInitialData()

      const timer = setTimeout(() => {
        loadAdditionalData()
      }, 500)

      return () => clearTimeout(timer)

    }
  }, [memoizedSearchParams])

  return {
    holeType,
    setHoleType,
    isInitialLoading,
    isWeekAvgPriceByRegionLoading,  // 추가
    data,
    loadInitialData,
    refreshWeekData,
    getWeekAvgPriceByRegion, // 필요 시 직접 호출 가능하게 노출
  }
}